import {
  infiniteQueryOptions,
  keepPreviousData,
  queryOptions,
} from "react-query";

import { reactQueryGet } from "@/react/helpers/backendRequestHelpers";
import { internalApi } from "@/react/helpers/urlHelpers";
import { aiAgentKey } from "@/react/lib/queries/aiAgents";
import type {
  AvatarSitePage,
  PageList,
  SelectedAvatarSitePage,
} from "@/react/lib/queries/avatarSitePages.types";
import { queryClient } from "@/react/providers/QueryClient/reactQueryConfig";

const sortedIds = (ids: number[]) =>
  [...ids].sort((left, right) => left - right).join(",");

export const avatarSitePageQueries = {
  list: ({
    page,
    query,
    excludedIds = [],
  }: {
    page: number;
    query: string;
    excludedIds?: number[];
  }) => {
    const excludedSitePageIds = sortedIds(excludedIds);
    return queryOptions({
      queryKey: aiAgentKey.key(
        "site-pages",
        "list",
        page,
        query,
        excludedSitePageIds,
      ),
      refetchOnWindowFocus: true,
      queryFn: () =>
        reactQueryGet<PageList<AvatarSitePage>>(
          internalApi.communityBot.sitePages.index({
            params: { page, query, exclude_site_page_ids: excludedSitePageIds },
          }),
        ),
    });
  },
  available: ({
    query,
    excludedIds = [],
  }: {
    query: string;
    excludedIds?: number[];
  }) => {
    const excludedSitePageIds = sortedIds(excludedIds);
    return infiniteQueryOptions({
      queryKey: aiAgentKey.key(
        "site-pages",
        "available",
        query,
        excludedSitePageIds,
      ),
      initialPageParam: 1,
      refetchOnWindowFocus: true,
      queryFn: ({ pageParam = 1 }: { pageParam?: number }) =>
        reactQueryGet<PageList<AvatarSitePage>>(
          internalApi.communityBot.sitePages.index({
            params: {
              page: pageParam,
              query,
              exclude_site_page_ids: excludedSitePageIds,
            },
          }),
        ),
      getNextPageParam: lastPage =>
        lastPage.has_next_page ? lastPage.page + 1 : undefined,
    });
  },
  selected: ({ ids }: { ids: number[] }) => {
    const sitePageIds = sortedIds(ids);
    return queryOptions<PageList<SelectedAvatarSitePage>>({
      queryKey: aiAgentKey.key("site-pages", "selected", sitePageIds),
      placeholderData: keepPreviousData,
      refetchOnWindowFocus: true,
      queryFn: () =>
        reactQueryGet<PageList<SelectedAvatarSitePage>>(
          internalApi.communityBot.sitePages.index({
            params: sitePageIds ? { site_page_ids: sitePageIds } : {},
          }),
        ),
    });
  },
};

export const avatarSitePageInvalidations = {
  selected: () =>
    queryClient.invalidateQueries({
      queryKey: aiAgentKey.key("site-pages", "selected"),
    }),
};
